import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { Paperclip, Send, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { attachmentsApi, uploadFile } from "@/api/attachments";
import type { Message } from "@/api/messages";
import { useWSStore } from "@/stores/ws";

type Props = {
  disabled?: boolean;
  disabledReason?: string;
  roomId?: string;
  onSend: (payload: {
    type: Message["type"];
    content_text?: string;
    attachment_id?: string;
  }) => void;
  /** Permite al padre inyectar texto al input (ej. desde un Reply). */
  registerPrefill?: (fn: (text: string) => void) => void;
};

type SelectedFile = {
  file: File;
  msgType: Message["type"];
};

function detectType(mime: string): Message["type"] {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("video/")) return "video";
  return "file";
}

export function MessageInput({ disabled, disabledReason, roomId, onSend, registerPrefill }: Props) {
  const [text, setText] = useState("");
  const [selected, setSelected] = useState<SelectedFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lastTypingSentRef = useRef(0);
  const sendTyping = useWSStore((s) => s.sendTyping);

  // Padre puede prefijar texto al input (ej. quote del mensaje a responder).
  useEffect(() => {
    if (!registerPrefill) return;
    registerPrefill((next: string) => {
      setText((prev) => (prev ? prev + "\n\n" + next : next));
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(el.value.length, el.value.length);
        }
      });
    });
  }, [registerPrefill]);

  const fireTyping = () => {
    if (!roomId || disabled) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 2000) return; // throttle 2s
    const fn = useWSStore.getState().sendTyping;
    if (!fn) {
      // WS aún no listo — retry tras 200ms para no perder el primer typing.
      setTimeout(() => {
        const fn2 = useWSStore.getState().sendTyping;
        if (fn2 && roomId) fn2(roomId);
      }, 200);
      return;
    }
    lastTypingSentRef.current = now;
    fn(roomId);
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelected({ file, msgType: detectType(file.type || "application/octet-stream") });
    e.target.value = ""; // reset para poder volver a elegir el mismo
  };

  const clearFile = () => setSelected(null);

  const submit = async () => {
    if (disabled || uploading) return;
    if (!text.trim() && !selected) return;

    // Si hay archivo: presign + upload + commit + send
    if (selected) {
      setUploading(true);
      try {
        const presign = await attachmentsApi.presign({
          mime: selected.file.type || "application/octet-stream",
          size_bytes: selected.file.size,
          original_filename: selected.file.name,
        });
        await uploadFile(selected.file, presign.upload_url);
        await attachmentsApi.commit(presign.attachment_id);
        onSend({
          type: selected.msgType,
          content_text: text.trim() || undefined,
          attachment_id: presign.attachment_id,
        });
        setSelected(null);
        setText("");
      } catch (e) {
        alert((e as Error).message);
      } finally {
        setUploading(false);
      }
      return;
    }

    onSend({ type: "text", content_text: text.trim() });
    setText("");
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="border-t border-slate-200 bg-white px-3 py-2">
      {disabled && disabledReason && (
        <div className="mb-2 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800">
          {disabledReason}
        </div>
      )}
      {selected && (
        <div className="mb-2 flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm">
          <Paperclip className="size-4 text-slate-500" />
          <span className="flex-1 truncate">
            {selected.file.name} <span className="text-slate-400">({Math.round(selected.file.size / 1024)} KB)</span>
          </span>
          <button type="button" onClick={clearFile} className="text-slate-500 hover:text-red-600">
            <X className="size-4" />
          </button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
          title="Adjuntar archivo"
        >
          <Paperclip className="size-5 text-slate-600" />
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={onFileChange}
          accept="image/*,audio/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.*,application/vnd.ms-excel,text/plain,application/zip"
        />
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            fireTyping();
          }}
          onKeyDown={onKey}
          disabled={disabled || uploading}
          rows={1}
          placeholder={disabled ? "" : "Escribí un mensaje (Enter para enviar, Shift+Enter nueva línea)"}
          className="flex-1 resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:bg-slate-50"
          style={{ maxHeight: "120px" }}
        />
        <Button
          type="button"
          size="icon"
          onClick={submit}
          disabled={disabled || uploading || (!text.trim() && !selected)}
          title="Enviar"
        >
          <Send className="size-4" />
        </Button>
      </div>
    </div>
  );
}
